import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Badge,
  Box,
  Heading,
  Text,
  VStack,
  Divider,
  Stack,
  HStack,
  Button,
  Grid,
  Flex,
  Spinner,
  Center,
} from "@chakra-ui/react";
import {
  useAdminApplication,
  useCreateReview,
  type ApplicationReview,
} from "@/api/admin/applications";
import { allVisionConditionLabels } from "@/api/application/application";
import { useUser } from "@/contexts/UserContext";
import { formatDate } from "@/utils/dateFormat";
import { getCountryName, formatStatus, calculateAge, formatBoolean } from "./utils";

// Helper to format single eye visual acuity based on country
const formatSingleEyeAcuity = (value: string | null, country: string | null): string => {
  if (!value) return "—";

  switch (country) {
    case "DE":
      return `${value}%`;
    case "FR":
      return value;
    case "GB":
    case "UK":
      return `6/${value}`;
    default:
      return `20/${value}`;
  }
};

// Helper to format driving status
const formatDriving = (driving: string | null): string => {
  if (!driving) return "—";
  const drivingMap: Record<string, string> = {
    yes: "Still drive without restrictions",
    yes_with_limitations: "Drive with limitations (daylight only, familiar routes, etc.)",
    no_due_to_vision: "Stopped driving due to vision problems",
    no_other: "Don't drive (other reasons or never drove)",
  };
  return drivingMap[driving] || driving;
};

// Helper to format reviewer name
const formatReviewerName = (reviewer: ApplicationReview["reviewer"]): string => {
  if (reviewer.first_name && reviewer.last_name) {
    return `${reviewer.first_name} ${reviewer.last_name}`;
  }
  return "Unknown Reviewer";
};

export const PatientDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: application, isLoading, error } = useAdminApplication(id);
  const { user } = useUser();
  const createReview = useCreateReview();

  const canReview = user?.is_reviewer === true;

  // Back-to-admin: restore the filtered/sorted /admin URL if we have one
  // (Admin/index.tsx writes its current URL to sessionStorage on every render).
  // Otherwise fall back to a clean /admin in the user's current locale.
  const handleBack = () => {
    const saved = sessionStorage.getItem("admin_last_url");
    if (saved && /^\/(de\/)?admin(\?|$)/.test(saved)) {
      navigate(saved);
    } else {
      const localePrefix = location.pathname.startsWith("/de/") ? "/de" : "";
      navigate(`${localePrefix}/admin`);
    }
  };

  const handleReview = (potentialCandidate: boolean) => {
    if (!id) return;
    createReview.mutate({ applicationId: id, potentialCandidate });
  };

  if (isLoading) {
    return (
      <Center py={20}>
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Box padding={8}>
        <VStack spacing={4}>
          <Heading>Error Loading Application</Heading>
          <Text color="red.500">
            {error instanceof Error ? error.message : "Unknown error occurred"}
          </Text>
          <Button onClick={handleBack} colorScheme="blue">
            Back to Applications
          </Button>
        </VStack>
      </Box>
    );
  }

  if (!application) {
    return (
      <Box padding={8}>
        <VStack spacing={4}>
          <Heading>Application Not Found</Heading>
          <Text>No application found for ID: {id}</Text>
          <Button onClick={handleBack} colorScheme="blue">
            Back to Applications
          </Button>
        </VStack>
      </Box>
    );
  }

  const statusInfo = formatStatus(application.status);

  return (
    <Box padding={[4, 6, 8]} maxWidth="container.xl" margin="0 auto">
      <VStack align="stretch" spacing={2} marginBottom={4}>
        <Button
          variant="link"
          size="sm"
          alignSelf="flex-start"
          onClick={handleBack}
          colorScheme="blue"
        >
          ← Applications
        </Button>
        <HStack spacing={3} alignItems="center">
          <Heading size="lg">Application {application.id.slice(0, 8)}</Heading>
          <Badge colorScheme={statusInfo.colorScheme} fontSize="md" paddingX={2} paddingY={1}>
            {statusInfo.text}
          </Badge>
        </HStack>
      </VStack>
      <Divider />
      <Grid gap={8} paddingBlockStart={8} templateColumns={["1fr", "1fr", "1fr", "1fr 1fr"]}>
        <VStack align="stretch" gap={8}>
          {/* Application Metadata */}
          <Box>
            <Heading size="md" marginBottom={4}>
              Application Information
            </Heading>
            <Stack spacing={3}>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Application ID:
                </Text>
                <Text>{application.id}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Applicant Type:
                </Text>
                <Text>{application.applicant_type === "self" ? "Self" : "Proxy"}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Submitted:
                </Text>
                <Text>{formatBoolean(application.is_submitted)}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Submitted At:
                </Text>
                <Text>{formatDate(application.submitted_at, "long")}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Patient Country:
                </Text>
                <Text>{getCountryName(application.patient_country)}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Patient Age:
                </Text>
                <Text>{calculateAge(application.patient_date_of_birth, true)}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Willing to Travel:
                </Text>
                <Text>{formatBoolean(application.personal_travel_willing)}</Text>
              </HStack>
            </Stack>
          </Box>

          <Divider />

          {/* Vision Conditions */}
          <Box>
            <Heading size="md" marginBottom={4}>
              Vision Conditions
            </Heading>
            {application.vision_conditions?.length > 0 ? (
              <VStack as="ul" align="start" spacing={2} marginInlineStart={8} paddingBlockEnd={2}>
                {application.vision_conditions.map((condition: string) => {
                  const label =
                    Object.entries(allVisionConditionLabels).find(
                      ([key]) => key === condition,
                    )?.[1] || condition;
                  return (
                    <Text as="li" key={condition}>
                      {label}
                    </Text>
                  );
                })}
              </VStack>
            ) : (
              <Text color="gray.500">No vision conditions specified</Text>
            )}
            {application.vision_macular_degeneration_type && (
              <Box marginTop={3}>
                <Text fontWeight="medium">Macular Degeneration Type:</Text>
                <Text>{application.vision_macular_degeneration_type}</Text>
              </Box>
            )}
            {application.vision_other_details && (
              <Box marginTop={3}>
                <Text fontWeight="medium">Other Details:</Text>
                <Text>{application.vision_other_details}</Text>
              </Box>
            )}
          </Box>

          <Divider />

          {/* Visual Acuity */}
          <Box>
            <Heading size="md" marginBottom={4}>
              Visual Acuity
            </Heading>
            <Stack spacing={3}>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Visual Acuity Known:
                </Text>
                <Text>{formatBoolean(application.vision_acuity_known)}</Text>
              </HStack>
              {application.vision_acuity_known === true && (
                <>
                  <HStack>
                    <Text fontWeight="medium" width="200px">
                      Left Eye:
                    </Text>
                    <Text>
                      {formatSingleEyeAcuity(
                        application.vision_acuity_left,
                        application.patient_country,
                      )}
                    </Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" width="200px">
                      Right Eye:
                    </Text>
                    <Text>
                      {formatSingleEyeAcuity(
                        application.vision_acuity_right,
                        application.patient_country,
                      )}
                    </Text>
                  </HStack>
                </>
              )}
              {application.vision_acuity_known === false && (
                <>
                  <HStack>
                    <Text fontWeight="medium" width="200px">
                      Read normal print:
                    </Text>
                    <Text>{formatBoolean(application.vision_can_read_normal_print)}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" width="200px">
                      Read headlines:
                    </Text>
                    <Text>{formatBoolean(application.vision_can_read_headlines)}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" width="200px">
                      See if room light is on:
                    </Text>
                    <Text>{formatBoolean(application.vision_can_see_indoors)}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" width="200px">
                      Localize person:
                    </Text>
                    <Text>{formatBoolean(application.vision_can_localize_person)}</Text>
                  </HStack>
                </>
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Driving */}
          {application.vision_driving && (
            <>
              <Box>
                <Heading size="md" marginBottom={4}>
                  Driving
                </Heading>
                <Text>{formatDriving(application.vision_driving)}</Text>
              </Box>
              <Divider />
            </>
          )}

          {/* Anything else the applicant wanted us to know (final step) */}
          {application.additional_info && (
            <>
              <Box>
                <Heading size="md" marginBottom={4}>
                  Anything Else We Should Know
                </Heading>
                <Text whiteSpace="pre-wrap">{application.additional_info}</Text>
              </Box>
              <Divider />
            </>
          )}

          {/* Consent Information */}
          <Box as="details">
            <Text as="summary" fontWeight="bold" fontSize="lg" cursor="pointer" marginBottom={4}>
              Consent Information
            </Text>
            <Stack spacing={3}>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Consent Locked:
                </Text>
                <Text>{formatBoolean(application.consent_locked)}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Consent Health Collection:
                </Text>
                <Text>{formatBoolean(application.consent_health_collection)}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Consent Health Sharing:
                </Text>
                <Text>{formatBoolean(application.consent_health_sharing)}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Consent Contact:
                </Text>
                <Text>{formatBoolean(application.consent_contact)}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Consent Contact Email:
                </Text>
                <Text>{formatBoolean(application.consent_contact_email)}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Consent Contact Phone:
                </Text>
                <Text>{formatBoolean(application.consent_contact_phone)}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="medium" width="200px">
                  Consent Contact Recurring:
                </Text>
                <Text>{formatBoolean(application.consent_contact_recurring)}</Text>
              </HStack>
            </Stack>
          </Box>

          {/* Proxy Information - only show if applicant_type is "proxy" */}
          {application.applicant_type === "proxy" && (
            <>
              <Divider />
              <Box as="details">
                <Text
                  as="summary"
                  fontWeight="bold"
                  fontSize="lg"
                  cursor="pointer"
                  marginBottom={4}
                >
                  Proxy Information
                </Text>
                <Stack spacing={3}>
                  <HStack>
                    <Text fontWeight="medium" width="200px">
                      Proxy Country:
                    </Text>
                    <Text>{getCountryName(application.proxy_country)}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" width="200px">
                      Proxy Age:
                    </Text>
                    <Text>{calculateAge(application.proxy_date_of_birth, true)}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" width="200px">
                      Personal Country of Residence:
                    </Text>
                    <Text>{getCountryName(application.personal_country_of_residence)}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" width="200px">
                      Applicant Is Adult:
                    </Text>
                    <Text>{formatBoolean(application.applicant_is_adult)}</Text>
                  </HStack>
                </Stack>
              </Box>
            </>
          )}
        </VStack>
        <VStack
          height="max-content"
          backgroundColor="gray.50"
          padding={8}
          borderRadius="md"
          align="stretch"
          gap={4}
          position="sticky"
          top={24}
        >
          <Heading size="md">Application Review</Heading>

          {/* Review buttons - only shown to reviewers when application is pending */}
          {canReview && application.status === "pending" && (
            <>
              <Flex
                direction={["column", "column", "row"]}
                gap={8}
                justifyContent="space-between"
                paddingBlock={2}
                width="full"
                alignItems="center"
              >
                <Button
                  size="lg"
                  width="full"
                  colorScheme="green"
                  onClick={() => handleReview(true)}
                  isLoading={createReview.isLoading}
                >
                  Potential Candidate
                </Button>
                <Text minWidth="max-content" textAlign="center">
                  Clinical Application:
                  <br />
                  <strong>PRIMA-378</strong>
                </Text>
                <Button
                  size="lg"
                  width="full"
                  colorScheme="red"
                  onClick={() => handleReview(false)}
                  isLoading={createReview.isLoading}
                >
                  Not a Candidate
                </Button>
              </Flex>
              {createReview.isError && (
                <Text color="red.500" fontSize="sm">
                  {createReview.error instanceof Error
                    ? createReview.error.message
                    : "Failed to submit review"}
                </Text>
              )}
              <Divider />
            </>
          )}

          {/* Review history */}
          <Text fontWeight="medium" color="gray.600">
            Review History
          </Text>
          {application.reviews.length === 0 ? (
            <Text color="gray.500" fontSize="sm">
              No reviews yet.
            </Text>
          ) : (
            <Stack spacing={3}>
              {application.reviews.map((review) => (
                <Box key={review.id}>
                  <HStack>
                    <Text
                      as="span"
                      color={review.potential_candidate ? "green.600" : "red.600"}
                      fontWeight="bold"
                    >
                      {review.potential_candidate ? "Potential Candidate" : "Not a Candidate"}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="gray.600">
                    by {formatReviewerName(review.reviewer)} on {formatDate(review.created_at)}
                  </Text>
                </Box>
              ))}
            </Stack>
          )}
        </VStack>
      </Grid>
    </Box>
  );
};
